/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { Atom, atom } from 'jotai';
import { atomFamily, unwrap } from 'jotai/utils';
import { activeSeriesDriversSelector } from 'jotai/driver';
import { fastestLapsService } from 'services/fastestLaps/FastestLapsService';
import _ from 'lodash';
import { FastDataResponse, LapAnalysisRequest, LapAnalysisSingle, OriginType, RunType } from 'models/viewModels';
import { activeEventSelector, activeSessionSelector } from 'jotai/session';

type FastestLapsParams = {
  xLap?: number;
  threshold?: number;
  lastTimeUpdated: number;
  eventId: string;
  sessionType: RunType;
  isReplay: boolean;
  dataSource: OriginType;
  isConsecutive: boolean;
};

export type FastestLapsTableRow = {
  vehicleNumber: string;
  vehicleManufacturer: string;
  fastLapRank?: number;
  fastLap?: number;
  fastLapDiff?: number;
  fast10VehicleNumber: string;
  fast10VehicleManufacturer: string;
  fast10?: number;
  fast10Diff?: number;
  fast20VehicleNumber: string;
  fast20VehicleManufacturer: string;
  fast20?: number;
  fast20Diff?: number;
  fast30VehicleNumber: string;
  fast30VehicleManufacturer: string;
  fast30?: number;
  fast30Diff?: number;
  fastXVehicleNumber: string;
  fastXVehicleManufacturer: string;
  fastX?: number;
  fastXDiff?: number;
};

const createRow = (index: number, rows: FastestLapsTableRow[]): FastestLapsTableRow => {
  const fastLapRank = index;
  let row = rows.find((item) => item.fastLapRank === fastLapRank);
  if (!row) {
    row = { fastLapRank } as FastestLapsTableRow;
    rows.push(row);
  }
  return row;
};

const addVehicleNumber = (row: FastestLapsTableRow, numLaps: number, lapData: string, xLap?: number) => {
  switch (numLaps) {
    case 1:
      row.vehicleNumber = lapData;
      break;
    case 10:
      row.fast10VehicleNumber = lapData;
      break;
    case 20:
      row.fast20VehicleNumber = lapData;
      break;
    case 30:
      row.fast30VehicleNumber = lapData;
      break;
    default:
      break;
  }
  if (numLaps === xLap) {
    row.fastXVehicleNumber = lapData;
  }
};

const addVehicleManufacturer = (
  row: FastestLapsTableRow,
  numLaps: number,
  lapData: string,
  xLap: number | undefined
) => {
  switch (numLaps) {
    case 1:
      row.vehicleManufacturer = lapData;
      break;
    case 10:
      row.fast10VehicleManufacturer = lapData;
      break;
    case 20:
      row.fast20VehicleManufacturer = lapData;
      break;
    case 30:
      row.fast30VehicleManufacturer = lapData;
      break;
    default:
      break;
  }
  if (numLaps === xLap) {
    row.fastXVehicleManufacturer = lapData;
  }
};

const addLapData = (row: FastestLapsTableRow, numLaps: number, lapData?: number, xLap?: number) => {
  switch (numLaps) {
    case 1:
      row.fastLap = lapData;
      break;
    case 10:
      row.fast10 = lapData;
      break;
    case 20:
      row.fast20 = lapData;
      break;
    case 30:
      row.fast30 = lapData;
      break;
    default:
      break;
  }
  if (numLaps === xLap) {
    row.fastX = lapData;
  }
};

const addLapDiffData = (row: FastestLapsTableRow, numLaps: number, lapData?: number, xLap?: number) => {
  switch (numLaps) {
    case 1:
      row.fastLapDiff = lapData;
      break;
    case 10:
      row.fast10Diff = lapData;
      break;
    case 20:
      row.fast20Diff = lapData;
      break;
    case 30:
      row.fast30Diff = lapData;
      break;
    default:
      break;
  }
  if (numLaps === xLap) {
    row.fastXDiff = lapData;
  }
};

const fastDataSelectorFamily = atomFamily<FastestLapsParams, Atom<FastDataResponse | undefined>>(
  (params: FastestLapsParams) =>
    unwrap(
      atom<Promise<FastDataResponse | undefined>>(async (get) => {
        const session = get(activeSessionSelector);
        const event = get(activeEventSelector);
        if (session && event) {
          const request: LapAnalysisRequest = {
            requestedLapNumber: params.xLap,
            threshold: params.threshold,
            streamInfo: {
              originType: params.dataSource,
              seriesType: Number(session.seriesId),
              runType: params.sessionType,
              raceId: event.eventId,
              sessionId: session.sessionId,
            },
            isReplay: params.isReplay,
            isConsecutive: params.isConsecutive,
          };

          return await fastestLapsService.getFastestLaps(request);
        }
      })
    ),
  (a, b) => _.isEqual(a, b)
);

export const defaultFastDataThresholdSelectorFamily = atomFamily<FastestLapsParams, Atom<number | undefined>>(
  (params: FastestLapsParams) =>
    atom<number | undefined>((get) => {
      const threshold = get(fastDataSelectorFamily(params))?.defaultThreshold;
      return threshold;
    }),
  (a, b) => _.isEqual(a, b)
);

export const fastestLapsSelectorFamily = atomFamily<FastestLapsParams, Atom<Promise<FastestLapsTableRow[]>>>(
  (params: FastestLapsParams) =>
    atom<Promise<FastestLapsTableRow[]>>(async (get) => {
      const manufacturers = get(activeSeriesDriversSelector)?.reduce((manufacturers, driver) => {
        manufacturers[driver.vehicleNumber] = driver.manufacturer;
        return manufacturers;
      }, {} as Record<string, string>);
      const laps = get(fastDataSelectorFamily(params));
      if (!laps) return [];
      const data = Object.entries(laps.fastDataMap || {})
        .reduce((previousValue, [numberOfLaps, dataObject]) => {
          const numLaps = parseInt(numberOfLaps);
          Object.entries(dataObject.lapData).forEach(([vehicleNumber, lapData]) => {
            const lapAnalysisSingle: LapAnalysisSingle = lapData;
            const row = createRow(lapAnalysisSingle.lapRank, previousValue);
            addVehicleNumber(row, numLaps, vehicleNumber, params.xLap);
            addVehicleManufacturer(row, numLaps, manufacturers ? manufacturers[vehicleNumber] : '', params.xLap);
            addLapData(row, numLaps, lapAnalysisSingle.lapTime, params.xLap);
            addLapDiffData(row, numLaps, lapAnalysisSingle.lapDiff, params.xLap);
          });

          return previousValue;
        }, [] as FastestLapsTableRow[])
        .sort((a, b) => (a.fastLapRank as number) - (b.fastLapRank as number));

      return data;
    }),
  (a, b) => _.isEqual(a, b)
);
